import { mapDTO } from '@twistezo/ts-dto-mapper'
import { isFloat, isInt, isJSON } from 'validator'

import {
  Schema,
  SchemaAdUnitBidderParams,
  SchemaAdUnitBidderProps,
} from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { AdUnitBidder } from 'features/globalSetup/types/adUnitBidder'
import { parseValidJSON } from 'utils/json'
import { AdUnitBidderAttribute, Variables } from './types'

type From = Schema
type To = Variables

export const toDTO = (data: From, dirtyRowsIds?: AdUnitBidder['id'][]): To =>
  mapDTO<From, To>({ from: data }).transform((data: From): To => {
    const {
      adUnitBidders,
      domainIds: { domainBidderId },
      ...rest
    } = data

    const parsedAttributes: AdUnitBidderAttribute[] = attributesToDTO(adUnitBidders)

    let filteredByDirty: AdUnitBidderAttribute[] = []
    if (dirtyRowsIds !== undefined && dirtyRowsIds.length > 0) {
      filteredByDirty = parsedAttributes.filter(({ id }: AdUnitBidderAttribute) =>
        dirtyRowsIds.includes(id),
      )
    }

    return {
      adUnitBiddersAttributes: filteredByDirty,
      id: domainBidderId.value,
      ...rest,
    }
  })

const attributesToDTO = (adUnitBidders: Schema['adUnitBidders']): AdUnitBidderAttribute[] =>
  Object.entries(adUnitBidders).map(
    ([key, { csEnabled, csParams, s2sEnabled, s2sParams }]: [
      key: keyof Schema['adUnitBidders'],
      SchemaAdUnitBidderProps,
    ]): AdUnitBidderAttribute => ({
      csEnabled,
      csParams: paramsToDTO(csParams),
      id: key,
      s2sEnabled,
      s2sParams: paramsToDTO(s2sParams),
    }),
  )

const paramsToDTO = (params: SchemaAdUnitBidderParams): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]: [string, string | undefined]): [string, unknown] => [
      key,
      paramToDTO(value),
    ]),
  )

const paramToDTO = (value: string | undefined): unknown => {
  let parsedValue: null | number | string | unknown = null

  if (value === undefined || value.length === 0) {
    parsedValue = null
  } else if (isInt(value)) {
    parsedValue = Number(value)
  } else if (isFloat(value, { locale: 'pl-PL' })) {
    parsedValue = Number(value.replace(',', '.'))
  } else if (isJSON(value)) {
    parsedValue = parseValidJSON(value)
  } else {
    parsedValue = value
  }

  return parsedValue
}
